import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Wrench } from 'lucide-react';

import { API_URL } from '../utils/api';

const TradieCarousel = () => {
  const [tradies, setTradies] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchAdvertisedTradies();
  }, []);

  useEffect(() => {
    // Auto-scroll animation
    const scroll = scrollRef.current;
    if (!scroll || tradies.length === 0) return;

    let animationId;
    let scrollPosition = 0;
    const speed = 0.5; // pixels per frame

    const animate = () => {
      scrollPosition += speed;
      
      // Reset when we've scrolled past the first set
      if (scrollPosition >= scroll.scrollWidth / 2) {
        scrollPosition = 0;
      }
      
      scroll.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    // Pause on hover
    const handleMouseEnter = () => cancelAnimationFrame(animationId);
    const handleMouseLeave = () => {
      animationId = requestAnimationFrame(animate);
    };

    scroll.addEventListener('mouseenter', handleMouseEnter);
    scroll.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationId);
      scroll.removeEventListener('mouseenter', handleMouseEnter);
      scroll.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [tradies]);

  const fetchAdvertisedTradies = async () => {
    try {
      // Fetch from promotions/active - only shows tradies who PAID for featured 
      // or were manually added by admin (NOT free-for-life subscription holders)
      const res = await axios.get(`${API_URL}/api/promotions/active`);
      setTradies(res.data.featured_tradies || []);
    } catch (err) {
      console.error('Failed to fetch featured tradies', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || tradies.length === 0) {
    return null; // Don't show if no featured tradies
  }

  // Duplicate the array to create seamless loop
  const displayTradies = [...tradies, ...tradies];

  return (
    <div className="bg-stone-100 border-t border-stone-200 py-4">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-2 mb-3">
          <Wrench className="h-4 w-4 text-teal-600" />
          <span className="text-sm font-medium text-stone-600">Featured Tradies</span>
        </div>
        
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-hidden"
          style={{ scrollBehavior: 'auto' }}
        >
          {displayTradies.map((tradie, index) => (
            <Link
              key={`${tradie.id}-${index}`}
              to={`/profile/${tradie.id}`}
              className="flex-shrink-0 w-36 group"
            >
              <div className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                {/* Profile Photo */}
                <div className="w-20 h-20 mx-auto rounded-full bg-stone-100 overflow-hidden mb-2">
                  {tradie.profile_photo ? (
                    <img
                      src={tradie.profile_photo}
                      alt={tradie.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-stone-400">
                      {tradie.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                
                {/* Info */}
                <div className="text-center">
                  <p className="text-xs font-medium text-stone-900 truncate">{tradie.name}</p>
                  <p className="text-xs text-stone-500 truncate flex items-center justify-center gap-1 mt-0.5">
                    <MapPin className="h-3 w-3" />
                    {tradie.service_state || tradie.location || 'Australia'}
                  </p>
                  {tradie.trade_services && tradie.trade_services[0] && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-teal-50 text-teal-600 text-xs rounded-full truncate max-w-full">
                      {getServiceName(tradie.trade_services[0])}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

// Helper function to get readable service name
const getServiceName = (serviceId) => {
  const serviceMap = {
    'furniture-restoration': 'Furniture Restoration',
    'upholstery': 'Upholstery',
    'reupholstering': 'Reupholstering',
    'carpentry': 'Carpentry',
    'custom-furniture': 'Custom Furniture',
    'painting-finishing': 'Painting',
    'metalwork': 'Metalwork',
    'antique-restoration': 'Antique Restoration',
    'plumbing': 'Plumbing',
    'electrical': 'Electrical',
    'landscaping': 'Landscaping',
    'gardening': 'Gardening',
    'panel-beating': 'Panel Beating',
    'automotive-spray': 'Auto Spray',
    'marine-spray': 'Marine Spray',
    'tiling': 'Tiling',
    'plastering': 'Plastering',
    'roofing': 'Roofing',
    'fencing': 'Fencing',
    'concreting': 'Concreting',
    'flooring': 'Flooring',
    'cabinet-making': 'Cabinet Making',
    'glazing': 'Glazing',
    'air-conditioning': 'Air Con',
    'other': 'Trade Services'
  };
  return serviceMap[serviceId] || serviceId;
};

export default TradieCarousel;
